import React, { useMemo, useState } from "react";
import { ChevronDown, ChevronUp, GitBranch } from "lucide-react";
import AidExtensionFlowchart from "@/pages/AidExtensionFlowchart";
import { APPEARANCE_TYPE_OPTIONS } from "@/lib/billingConstants";
import { flowchartStartFromMatter } from "@/lib/extensionCheck";
import { plannerMatterReminders } from "@/lib/grantReminders";
import { getClaimAvailabilityTable } from "@/lib/aidPlannerCriminalFees";

// The Aid Extension Flowchart inside a matter: what the matter's reminders say, then the
// flowchart itself, filled in from the matter, for anything they do not settle.
export default function ExtensionCheckTile({ planner }) {
  const [open, setOpen] = useState(false);
  const reminders = useMemo(
    () => plannerMatterReminders(planner, { availabilityFor: getClaimAvailabilityTable }),
    [planner],
  );
  const start = useMemo(() => flowchartStartFromMatter(planner, APPEARANCE_TYPE_OPTIONS), [planner]);
  const headline = reminders[0];
  const clientName = planner.client?.fullName || [planner.client?.firstName, planner.client?.lastName].filter(Boolean).join(" ");

  return (
    <section className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden border-t-4 border-t-slate-800">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="w-full px-4 py-3 flex items-center justify-between gap-3 text-left hover:bg-slate-50"
      >
        <span className="flex items-center gap-2">
          <GitBranch className="w-4 h-4 text-slate-700" />
          <span className="text-sm font-bold text-slate-800">Extension check</span>
          <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${headline ? "bg-orange-100 text-orange-800" : "bg-emerald-100 text-emerald-800"}`}>
            {headline ? headline.label : "Nothing due"}
          </span>
        </span>
        <span className="flex items-center gap-1 text-xs font-semibold text-slate-500">
          {open ? "Hide" : "Check whether an extension is needed"}
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </span>
      </button>
      {open && (
        <div className="border-t border-slate-100 bg-slate-50 p-4">
          <AidExtensionFlowchart
            key={`${planner.matterId}:${start.matterType}:${start.request.aidInPlace}:${start.request.appearanceType}:${start.request.appearanceOther}`}
            embedded
            start={start}
            reminders={reminders}
            matterName={clientName}
          />
        </div>
      )}
    </section>
  );
}
